import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ChevronDown, Flag, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SneakDots = ({ level }) => (
  <span className="inline-flex gap-1" aria-label={`Sneakiness ${level} of 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <span key={n} className={`w-2 h-2 rounded-full ${n <= level ? "bg-primary" : "bg-border"}`} />
    ))}
  </span>
);

export default function ScamArchive() {
  const [scams, setScams] = useState([]);
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      axios
        .get(`${API}/api/scams`, { params: { q: query } })
        .then((res) => setScams(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  return (
    <section id="archive" className="py-24 lg:py-32 scroll-mt-20" data-testid="scam-archive-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="flex flex-wrap items-end justify-between gap-8 mb-12"
        >
          <div className="max-w-2xl">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-primary mb-4">
              No. 02 — The Archive
            </p>
            <h2 className="font-display font-semibold text-3xl/[1.1] sm:text-4xl/[1.1] lg:text-5xl/[1.1] tracking-tight" data-testid="scam-archive-heading">
              Every scam we've <span className="text-primary">called out.</span>
            </h2>
            <p className="mt-4 text-muted-foreground text-base md:text-lg leading-relaxed">
              A browsable library of every weekly alert. Search it before you tap that weird link.
            </p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={2} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search scams… try 'CRA' or 'e-transfer'"
              data-testid="scam-archive-search-input"
              className="w-full rounded-full border border-input bg-card pl-11 pr-4 py-3.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-shadow duration-200"
            />
          </div>
        </motion.div>

        <div className="border-t border-border" data-testid="scam-archive-list">
          {loading && scams.length === 0 && (
            <p className="py-10 text-muted-foreground text-sm" data-testid="scam-archive-loading">Digging through the files…</p>
          )}
          {!loading && scams.length === 0 && (
            <p className="py-10 text-muted-foreground text-sm" data-testid="scam-archive-empty">
              No scams match "{query}" — which is good news, honestly.
            </p>
          )}
          {scams.map((scam, i) => {
            const open = openId === scam.id;
            return (
              <div key={scam.id} className="border-b border-border" data-testid={`archive-item-${i}`}>
                <button
                  onClick={() => setOpenId(open ? null : scam.id)}
                  aria-expanded={open}
                  data-testid={`archive-toggle-${i}`}
                  className="w-full flex items-center gap-4 py-6 text-left group"
                >
                  <span className="font-display font-semibold text-primary text-sm w-10 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-display font-semibold text-lg md:text-xl tracking-tight group-hover:text-primary transition-colors duration-200">
                      {scam.title}
                    </span>
                    <span className="block text-xs text-muted-foreground mt-1">{scam.week_of}</span>
                  </span>
                  <SneakDots level={scam.severity} />
                  <ChevronDown className={`w-5 h-5 text-muted-foreground shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} strokeWidth={2} />
                </button>
                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                      data-testid={`archive-detail-${i}`}
                    >
                      <div className="pb-8 pl-14 pr-2 grid md:grid-cols-2 gap-8">
                        <div>
                          <p className="text-foreground/90 font-medium leading-relaxed mb-3">{scam.hook}</p>
                          <p className="text-muted-foreground text-sm leading-relaxed">{scam.description}</p>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <p className="flex items-center gap-2 font-display font-semibold text-sm mb-2">
                              <Flag className="w-3.5 h-3.5 text-primary" strokeWidth={2} /> Red flags
                            </p>
                            <ul className="space-y-1.5">
                              {scam.red_flags.map((f, j) => (
                                <li key={j} className="text-sm text-muted-foreground pl-5 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-2 before:h-2 before:rounded-full before:bg-accent">{f}</li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <p className="flex items-center gap-2 font-display font-semibold text-sm mb-2">
                              <Check className="w-3.5 h-3.5 text-primary" strokeWidth={2.5} /> What to do
                            </p>
                            <ul className="space-y-1.5">
                              {scam.what_to_do.map((w, j) => (
                                <li key={j} className="text-sm text-muted-foreground pl-5 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-2 before:h-2 before:rounded-full before:bg-secondary">{w}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
